/**
 * Color-class functions for badge/status rendering
 *
 * Color Strategy:
 * - Green: Success/Completed states
 * - Blue: Active/In Progress states
 * - Yellow/Orange: Warning/Pending states
 * - Red: Destructive/Cancelled states
 * - Purple: Special/Unique states
 * - Gray: Inactive/Archived states
 */

export type ColorClasses = { bg: string; text: string; border: string };

const DEFAULT_COLOR: ColorClasses = {
  bg: "bg-card-50",
  text: "text-gray-700 dark:text-gray-300",
  border: "border-gray-200 dark:border-gray-700",
};

/**
 * Maps status values to semantic color classes
 */
export const getStatusColorClasses = (status: string): ColorClasses => {
  const colorMap: Record<string, ColorClasses> = {
    // Contact Status
    lead: { bg: "bg-blue-50 dark:bg-blue-950/40", text: "text-blue-700 dark:text-blue-300", border: "border-blue-200 dark:border-blue-800" },
    prospect: { bg: "bg-cyan-50 dark:bg-cyan-950/40", text: "text-cyan-700 dark:text-cyan-300", border: "border-cyan-200 dark:border-cyan-800" },
    customer: { bg: "bg-green-50 dark:bg-green-950/40", text: "text-green-700 dark:text-green-300", border: "border-green-200 dark:border-green-800" },
    churned: { bg: "bg-red-50 dark:bg-red-950/40", text: "text-red-700 dark:text-red-300", border: "border-red-200 dark:border-red-800" },
    inactive: DEFAULT_COLOR,

    // Ticket Status
    new: { bg: "bg-blue-50 dark:bg-blue-950/40", text: "text-blue-700 dark:text-blue-300", border: "border-blue-200 dark:border-blue-800" },
    assigned: { bg: "bg-indigo-50 dark:bg-indigo-950/40", text: "text-indigo-700 dark:text-indigo-300", border: "border-indigo-200 dark:border-indigo-800" },
    in_progress: { bg: "bg-amber-50 dark:bg-amber-950/40", text: "text-amber-700 dark:text-amber-300", border: "border-amber-200 dark:border-amber-800" },
    waiting_for_customer: { bg: "bg-orange-50 dark:bg-orange-950/40", text: "text-orange-700 dark:text-orange-300", border: "border-orange-200 dark:border-orange-800" },
    waiting_for_vendor: { bg: "bg-orange-50 dark:bg-orange-950/40", text: "text-orange-700 dark:text-orange-300", border: "border-orange-200 dark:border-orange-800" },
    resolved: { bg: "bg-emerald-50 dark:bg-emerald-950/40", text: "text-emerald-700 dark:text-emerald-300", border: "border-emerald-200 dark:border-emerald-800" },
    closed: { bg: "bg-green-50 dark:bg-green-950/40", text: "text-green-700 dark:text-green-300", border: "border-green-200 dark:border-green-800" },
    cancelled: { bg: "bg-red-50 dark:bg-red-950/40", text: "text-red-700 dark:text-red-300", border: "border-red-200 dark:border-red-800" },

    // Task Status
    pending: { bg: "bg-yellow-50 dark:bg-yellow-950/40", text: "text-yellow-700 dark:text-yellow-300", border: "border-yellow-200 dark:border-yellow-800" },
    completed: { bg: "bg-green-50 dark:bg-green-950/40", text: "text-green-700 dark:text-green-300", border: "border-green-200 dark:border-green-800" },

    // Meeting Status
    scheduled: { bg: "bg-blue-50 dark:bg-blue-950/40", text: "text-blue-700 dark:text-blue-300", border: "border-blue-200 dark:border-blue-800" },
    rescheduled: { bg: "bg-purple-50 dark:bg-purple-950/40", text: "text-purple-700 dark:text-purple-300", border: "border-purple-200 dark:border-purple-800" },

    // Proposal Status
    draft: DEFAULT_COLOR,
    sent: { bg: "bg-blue-50 dark:bg-blue-950/40", text: "text-blue-700 dark:text-blue-300", border: "border-blue-200 dark:border-blue-800" },
    viewed: { bg: "bg-indigo-50 dark:bg-indigo-950/40", text: "text-indigo-700 dark:text-indigo-300", border: "border-indigo-200 dark:border-indigo-800" },
    accepted: { bg: "bg-green-50 dark:bg-green-950/40", text: "text-green-700 dark:text-green-300", border: "border-green-200 dark:border-green-800" },
    rejected: { bg: "bg-red-50 dark:bg-red-950/40", text: "text-red-700 dark:text-red-300", border: "border-red-200 dark:border-red-800" },
    expired: DEFAULT_COLOR,
  };

  return colorMap[status] ?? DEFAULT_COLOR;
};

/**
 * Maps priority values to semantic color classes
 */
export const getPriorityColorClasses = (priority: string): ColorClasses => {
  const colorMap: Record<string, ColorClasses> = {
    critical: { bg: "bg-red-50 dark:bg-red-950/40", text: "text-red-700 dark:text-red-300", border: "border-red-200 dark:border-red-800" },
    urgent: { bg: "bg-red-50 dark:bg-red-950/40", text: "text-red-700 dark:text-red-300", border: "border-red-200 dark:border-red-800" },
    high: { bg: "bg-orange-50 dark:bg-orange-950/40", text: "text-orange-700 dark:text-orange-300", border: "border-orange-200 dark:border-orange-800" },
    medium: { bg: "bg-yellow-50 dark:bg-yellow-950/40", text: "text-yellow-700 dark:text-yellow-300", border: "border-yellow-200 dark:border-yellow-800" },
    low: { bg: "bg-green-50 dark:bg-green-950/40", text: "text-green-700 dark:text-green-300", border: "border-green-200 dark:border-green-800" },
  };

  return colorMap[priority] ?? DEFAULT_COLOR;
};

/**
 * Maps time entry type values to semantic color classes
 */
export const getEntryTypeColorClasses = (entryType: string): ColorClasses => {
  const colorMap: Record<string, ColorClasses> = {
    billable: { bg: "bg-green-50 dark:bg-green-950/40", text: "text-green-700 dark:text-green-300", border: "border-green-200 dark:border-green-800" },
    non_billable: DEFAULT_COLOR,
    travel: { bg: "bg-blue-50 dark:bg-blue-950/40", text: "text-blue-700 dark:text-blue-300", border: "border-blue-200 dark:border-blue-800" },
    research: { bg: "bg-purple-50 dark:bg-purple-950/40", text: "text-purple-700 dark:text-purple-300", border: "border-purple-200 dark:border-purple-800" },
  };

  return colorMap[entryType] ?? DEFAULT_COLOR;
};

/**
 * Maps billable status (boolean) to semantic color classes
 */
export const getBillableStatusColorClasses = (isBillable: boolean): ColorClasses => {
  return isBillable
    ? { bg: "bg-green-50 dark:bg-green-950/40", text: "text-green-700 dark:text-green-300", border: "border-green-200 dark:border-green-800" }
    : DEFAULT_COLOR;
};

/**
 * Maps approval status (boolean) to semantic color classes
 */
export const getApprovalStatusColorClasses = (isApproved: boolean): ColorClasses => {
  return isApproved
    ? { bg: "bg-green-50 dark:bg-green-950/40", text: "text-green-700 dark:text-green-300", border: "border-green-200 dark:border-green-800" }
    : { bg: "bg-yellow-50 dark:bg-yellow-950/40", text: "text-yellow-700 dark:text-yellow-300", border: "border-yellow-200 dark:border-yellow-800" };
};

/**
 * Maps sync status values to semantic color classes
 */
export const getSyncStatusColorClasses = (syncStatus: string): ColorClasses => {
  const colorMap: Record<string, ColorClasses> = {
    pending: { bg: "bg-yellow-50 dark:bg-yellow-950/40", text: "text-yellow-700 dark:text-yellow-300", border: "border-yellow-200 dark:border-yellow-800" },
    synced: { bg: "bg-green-50 dark:bg-green-950/40", text: "text-green-700 dark:text-green-300", border: "border-green-200 dark:border-green-800" },
    failed: { bg: "bg-red-50 dark:bg-red-950/40", text: "text-red-700 dark:text-red-300", border: "border-red-200 dark:border-red-800" },
    disapproved: { bg: "bg-red-50 dark:bg-red-950/40", text: "text-red-700 dark:text-red-300", border: "border-red-200 dark:border-red-800" },
  };

  return colorMap[syncStatus] ?? DEFAULT_COLOR;
};
